import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Animatronic } from '../types/animatronic';
import { QUESTION_TYPES } from '../utils/ranking';
import type { QuestionType } from '../types/ranking';

interface RankingPair {
  animatronic_a: Animatronic;
  animatronic_b: Animatronic;
}

export function useRankingGame() {
  const [currentPair, setCurrentPair] = useState<RankingPair | null>(null);
  const [currentType, setCurrentType] = useState<QuestionType>(QUESTION_TYPES[0]);
  const [error, setError] = useState<Error | null>(null);

  const fetchNextPair = useCallback(async (skipRotation = false) => {
    try {
      setError(null);

      // Get total count first
      const { count } = await supabase
        .from('animatronics')
        .select('*', { count: 'exact', head: true })
        .eq('published', true);

      if (!count) throw new Error('No animatronics available');

      // Get two random animatronics
      const index1 = Math.floor(Math.random() * count);
      let index2 = Math.floor(Math.random() * (count - 1));
      if (index2 >= index1) index2++;

      const [{ data: data1 }, { data: data2 }] = await Promise.all([
        supabase
          .from('animatronics')
          .select('*')
          .eq('published', true)
          .range(index1, index1)
          .single(),
        supabase
          .from('animatronics')
          .select('*')
          .eq('published', true)
          .range(index2, index2)
          .single()
      ]);

      if (!data1 || !data2) throw new Error('Failed to get matchup');

      setCurrentPair({
        animatronic_a: data1,
        animatronic_b: data2
      });

      if (!skipRotation) {
        setCurrentType(prev => {
          const currentIndex = QUESTION_TYPES.indexOf(prev);
          return QUESTION_TYPES[(currentIndex + 1) % QUESTION_TYPES.length];
        });
      }
    } catch (err) {
      console.error('Error fetching matchup:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch matchup'));
    }
  }, []);

  const submitVote = useCallback(async (
    winnerId: string | null,
    isTie: boolean = false
  ) => {
    if (!currentPair) return;
    
    try {
      const { error: voteError } = await supabase
        .from('ranking_votes')
        .insert({
          animatronic_a_id: currentPair.animatronic_a.id,
          animatronic_b_id: currentPair.animatronic_b.id,
          winner_id: winnerId,
          is_tie: isTie,
          vote_type: currentType
        });

      if (voteError) throw voteError;
      
      // Get next pair
      await fetchNextPair();
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError(err instanceof Error ? err : new Error('Failed to submit vote'));
    }
  }, [currentPair, currentType, fetchNextPair]);

  const skipMatchup = useCallback(async () => {
    await fetchNextPair(true);
  }, [fetchNextPair]);

  useEffect(() => {
    fetchNextPair(true);
  }, [fetchNextPair]);

  return {
    currentPair,
    currentType,
    error,
    submitVote,
    skipMatchup
  };
}